<template>
  <RouterView
    v-slot="{ Component }"
    :class="{
      '!pb-[79px] md:pb-0': layoutName === 'default'
    }"
    class="min-h-svh"
  >
    <ConfigurationInitializer v-if="!skipInitialization" />
    <Component :is="layoutComponent[layoutName]">
      <Component :is="Component" v-if="appIsReady" />
    </Component>
    <LoadingApp v-if="showGlobalPreloader" />
    <Search />
  </RouterView>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onErrorCaptured, provide, type Ref } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import { Search } from '@/2_pages/Search'
import { ConfigurationInitializer, LoadingApp } from '@/3_widgets/Configuration'
import { useConfiguration } from '@/5_entities/Configuration'
import { appMessenger } from '@/5_entities/Configuration'
import { AbortError } from '@/6_shared/api'
import DefaultLayout from './ui/layout/DefaultLayout.vue'
import EmptyLayout from './ui/layout/EmptyLayout.vue'
import ErrorLayout from './ui/layout/ErrorLayout.vue'
import HomePageLayout from './ui/layout/HomePageLayout.vue'

const route = useRoute()

const layoutName = computed((): string => {
  if (!route.name) return ''

  return route.meta.layout ? (route.meta.layout as string) : 'default'
})

const layoutComponent: Record<
  string,
  typeof DefaultLayout | typeof EmptyLayout | typeof ErrorLayout | typeof HomePageLayout
> = {
  default: DefaultLayout,
  homePageLayout: HomePageLayout,
  emptyLayout: EmptyLayout,
  errorLayout: ErrorLayout
}

const skipInitialization = computed(() => !layoutName.value || layoutName.value === 'error')

const { globalLoading, showGlobalPreloader, appIsReady } = storeToRefs(useConfiguration())
provide<Ref<boolean>>('globalLoading', globalLoading)

const onErrorCapturedHandler = (error: Error) => {
  console.log(error.message)
  if (error instanceof AbortError || error?.name === 'AbortError') return

  if (error?.message) {
    toast.error(error.message)
  }
}

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  onErrorCapturedHandler(event.reason)
})

onErrorCaptured(onErrorCapturedHandler)

window.onbeforeunload = () => appMessenger.close()
</script>
